import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import Literal from 'components/literal';
import { ButtonSecondaryCompact, ButtonIcon } from 'components/buttons';
import { errorToast } from 'utils/toast';
import { Close, FileTypeZip } from 'components/SvgIcons';
import './styles.scss';

const UploadFiles = (props) => {
    const {
        multipleUpload = true,
        fileType = '*/*',
        filesLimit,
        onChange = () => null,
        input,
        value = [],
    } = props;

    const [files, setFiles] = useState(value.length ? value : []);
    const [deletedFiles, setDeletedFiles] = useState(0);

    const calcNumberOfFiles = (acceptedFiles) => {
        let result;

        if (filesLimit) {
            result = files.length + acceptedFiles.length - deletedFiles <= filesLimit;
        } else {
            result = true;
        }

        return result;
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: fileType,
        multiple: multipleUpload,
        onDrop: (acceptedFiles) => {
            if (calcNumberOfFiles(acceptedFiles)) {
                acceptedFiles.forEach((file) => {
                    const reader = new FileReader();

                    reader.onerror = () => {
                        errorToast('Error reading the file');
                    };

                    reader.onload = () => {
                        Object.assign(file, {
                            data: reader.result,
                            preview: URL.createObjectURL(file),
                        });

                        setFiles((prevFiles) => prevFiles.concat(file));
                    };

                    reader.readAsDataURL(file);
                });
            } else {
                errorToast(`Max number of files of ${filesLimit} is reached`);
            }
        },
    });

    // Observe files state and set the input
    useEffect(() => {
        if (files.length > 0) {
            input.onChange(files);
            onChange(files);
        }
    }, [files, onChange, input]);

    useEffect(
        () => () => {
            // Make sure to revoke the data uris to avoid memory leaks
            files.forEach((file) => URL.revokeObjectURL(file.preview));
        },
        [files],
    );

    const getIcon = (icon, image) => {
        switch (icon) {
            case 'application/zip, application/x-zip-compressed':
                return <FileTypeZip />;
            case 'image/*':
                return <img src={image} alt="" />;
            default:
                return '';
        }
    };

    const removeFile = (name) => {
        files.forEach((file, index) => {
            if (file.name === name) {
                files[index].deleted = true;
            }
        });

        setDeletedFiles((prevDeletedFiles) => prevDeletedFiles + 1);
        setFiles(files);
    };

    const renderFiles = files.map(
        (file) =>
            !file.deleted && (
                <div className="files__file" key={file.name}>
                    {getIcon(fileType, file.preview)}
                    <p>{file.name}</p>
                    {file.deleted}{' '}
                    <ButtonIcon icon={<Close />} onClick={() => removeFile(file.name)} />
                </div>
            ),
    );

    const showFiles = files.length > 0 && files.find((file) => file.deleted !== true);

    return (
        <div className="upload-files">
            {!showFiles && (
                <div
                    {...getRootProps()}
                    className={`
          dropzone 
          ${isDragActive ? ' dropzone--isActive' : ''}
        `}
                >
                    <input {...getInputProps()} />
                    <p>
                        <Literal literal="cfm_label_dran_and_drop" />{' '}
                        <span className="text-blue">
                            <Literal literal="cfm_label_dran_and_drop_and_update" />
                        </span>
                    </p>
                </div>
            )}

            {showFiles && (
                <Fragment>
                    {multipleUpload && (
                        <div {...getRootProps()} className="add-more-files">
                            <input {...getInputProps()} />
                            <ButtonSecondaryCompact>
                                <Literal literal="action_upload" />
                            </ButtonSecondaryCompact>
                        </div>
                    )}

                    <div className="files">{renderFiles}</div>
                </Fragment>
            )}
        </div>
    );
};

UploadFiles.propTypes = {
    fileType: PropTypes.string,
    filesLimit: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.string]),
    input: PropTypes.shape({}).isRequired,
    multipleUpload: PropTypes.bool,
    onChange: PropTypes.func,
};

export default UploadFiles;
